import { Partner } from 'src/types';
import { inIframe } from './browser';
import { ExchangeType, HealthPathwayType } from 'src/data';

/**
 * Helper function for special logic to handoff to freelancers union
 * We should only handoff in NYC counties when partner is specified and we're in an iframe
 * @param param0
 * @returns
 */
export function shouldHandoffToFreelancersUnion({
  fips,
  partner,
}: {
  fips: string;
  partner: string;
}) {
  const EXCLUDED_FIPS = ['36061', '36005', '36047', '36081', '36085'];
  return EXCLUDED_FIPS.includes(fips) && partner === Partner.FreelancersUnion && inIframe();
}

/**
 * Note: extend the ICHRA partner logic here if we want to
 */
export function isIchraPartner({ partner }: { partner?: string }) {
  const ICHRA_PARTNERS = [Partner.Decisely];
  return ICHRA_PARTNERS.includes(partner as Partner);
}

/**
 * Decisely members enroll by phone instead of signing up after choosing a plan
 */
export function isDeciselyPartner({ partner }: { partner?: string }) {
  return partner === Partner.Decisely;
}

/**
 * Only show off exchange details for ICHRA partners
 * - Currently, flagged to only show in dev
 */
export function shouldShowOffExchange({
  partner
}: { partner?: string }) {
  return isIchraPartner({ partner });
}

/**
 * Gets the default exchange type for a consumer.
 *
 * Non-ICHRA partners never see off-exchange plans, so they always default to on-exchange.
 *
 * For ICHRA partners (e.g. Decisely) the default depends on the state's exchange model,
 * which the backend deduces per-state and returns as `pathway`:
 *   - FFM (EDE state)              -> default to ON-exchange. In EDE states we prefer
 *                                     on-exchange enrollments; off-exchange is a fallback.
 *   - SBM / STATE_APP (SBE state)  -> default to OFF-exchange. Showing off-exchange first
 *                                     avoids the broker/assistor handoff friction on the
 *                                     state exchange platform.
 *
 * The pathway is only known once the session exists, so at session-creation time (no
 * pathway yet) this returns `undefined` for ICHRA partners — the caller should leave
 * `exchangeType` unset and apply the default once the pathway resolves.
 */
export function getDefaultExchangeType({
  partner,
  pathway,
}: {
  partner?: string;
  pathway?: HealthPathwayType | null;
}): ExchangeType | undefined {
  if (!isIchraPartner({ partner })) return ExchangeType.OnMarket;
  if (!pathway) return undefined;
  return pathway === HealthPathwayType.Ffm ? ExchangeType.OnMarket : ExchangeType.OffMarket;
}